import { metricsLogger } from '@frontend/tracking';

export type CacheOutcome = 'hit-fresh' | 'hit-stale' | 'miss';

const GUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const ISO_DATETIME_REGEX = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})/gi;
const ISO_DATE_REGEX = /\b\d{4}-\d{2}-\d{2}\b/g;

/**
 * Simplifies wetags expressions by collapsing repeated and(has:X, has:X) -> has:X
 */
function simplifyWetagsQuery(query: string): string {
  let prev = '';
  let curr = query;
  const repeatedPairRegex = /and\(\s*has:((?:<guid>:)?[a-zA-Z0-9_-]+)\s*,\s*has:\1\s*\)/g;
  while (prev !== curr) {
    prev = curr;
    curr = curr.replace(repeatedPairRegex, 'has:$1');
  }
  return curr;
}

/**
 * Recursively sanitizes primitive values, arrays, and objects:
 * - Replaces UUIDs with '<guid>'
 * - Replaces ISO datetimes with '<datetime>'
 * - Replaces ISO dates with '<date>'
 * - Collapses / deduplicates arrays of identical values (such as multiple GUIDs)
 * - Deterministically sorts object keys
 */
export function sanitizeValue(value: unknown): unknown {
  if (typeof value === 'string') {
    let s = value.replace(GUID_REGEX, '<guid>');
    s = s.replace(ISO_DATETIME_REGEX, '<datetime>');
    s = s.replace(ISO_DATE_REGEX, '<date>');
    if (s.includes('has:')) {
      s = simplifyWetagsQuery(s);
    }
    return s;
  }

  if (Array.isArray(value)) {
    const sanitized = value.map(sanitizeValue);
    const seen = new Set<string>();
    const unique: unknown[] = [];
    for (const item of sanitized) {
      const key = typeof item === 'object' && item !== null ? JSON.stringify(item) : String(item);
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(item);
      }
    }
    return unique;
  }

  if (typeof value === 'object' && value !== null) {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      const val = (value as Record<string, unknown>)[key];
      if (val !== undefined) {
        sorted[key] = sanitizeValue(val);
      }
    }
    return sorted;
  }

  return value;
}

function isStaticDescriptor(part: unknown): boolean {
  if (typeof part !== 'string') {
    return false;
  }
  const trimmed = part.trim();
  if (!trimmed || trimmed === '<guid>' || trimmed === '<datetime>' || trimmed === '<date>') {
    return false;
  }
  GUID_REGEX.lastIndex = 0;
  if (GUID_REGEX.test(trimmed)) {
    return false;
  }
  ISO_DATETIME_REGEX.lastIndex = 0;
  ISO_DATE_REGEX.lastIndex = 0;
  if (ISO_DATETIME_REGEX.test(trimmed) || ISO_DATE_REGEX.test(trimmed)) {
    return false;
  }
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    return false;
  }
  if (/^(?:and|or|not)\(/.test(trimmed) || /^has:/.test(trimmed)) {
    return false;
  }
  if (/^\d+$/.test(trimmed)) {
    return false;
  }
  return true;
}

/**
 * Normalizes a queryKey:
 * - Drops empty objects ({})
 * - Groups static descriptor strings at the front (e.g. ['paymentsMerchant', locationIds])
 * - Derives a clean dot-delimited queryName (e.g. 'schedule.appointments', 'paymentsMerchant')
 * - Formats the key into a pipe-delimited canonical string
 */
export function normalizeQueryKey(queryKey: unknown): { queryName: string; formattedKey: string } {
  if (!Array.isArray(queryKey)) {
    const sanitized = sanitizeValue(queryKey);
    const formatted =
      typeof sanitized === 'object' && sanitized !== null ? JSON.stringify(sanitized) : String(sanitized);
    const queryName = isStaticDescriptor(queryKey) ? String(queryKey) : 'unknown';
    return { queryName, formattedKey: formatted };
  }

  // Filter out empty objects ({})
  const parts = queryKey.filter(
    (p) => !(typeof p === 'object' && p !== null && !Array.isArray(p) && Object.keys(p).length === 0)
  );

  const staticDescriptors: string[] = [];
  const dynamicOrComplex: unknown[] = [];

  for (const part of parts) {
    if (isStaticDescriptor(part)) {
      staticDescriptors.push(String(part));
    } else {
      dynamicOrComplex.push(sanitizeValue(part));
    }
  }

  const queryName = staticDescriptors.length > 0 ? staticDescriptors.join('.') : 'unknown';

  const reordered = [...staticDescriptors, ...dynamicOrComplex];
  const formattedKey = reordered
    .map((part) => (typeof part === 'object' && part !== null ? JSON.stringify(part) : String(part)))
    .join('|');

  return { queryName, formattedKey };
}

/**
 * Tracks and reports cache hits, misses, and revalidations to telemetry.
 */
export function trackCacheMetric(queryKey: unknown, outcome: CacheOutcome) {
  try {
    const { queryName, formattedKey } = normalizeQueryKey(queryKey);

    metricsLogger.metric('query_cache_outcome', 1, {
      attributes: {
        queryName,
        queryKey: formattedKey,
        outcome,
        team: 'frontend-ops',
        category: 'infra',
      },
    });
  } catch (err) {
    // Avoid crashing the core application due to telemetry or serialization errors
    console.error('Error tracking query cache outcome metric', err);
  }
}
